// Static navigation and utility-page copy. Learning content and character names stay unchanged.
import featureNames from './featureNames';

const learningNavigation = {
  "en": {
    "common": {
      "backHome": "Back to training home",
      "level": "Level {level}",
      "score": "Points",
      "scoreValue": "{score} pt",
      "unknownDrill": "Unknown drill",
      "reading": "Reading",
      "short": "Short",
      "sentence": "Sentence",
      "grade": "Grade {grade}",
      "retry": "Retry",
      "speech": "Speech",
      "story": "Story",
      "previous": "Prev",
      "next": "Next"
    },
    "map": {
      "empty": "No flash cards yet. Create your first flash card to get started!",
      "searchLabel": "Search Q/A:",
      "searchRegion": "Card search",
      "searchPlaceholder": "Search questions, answers, or reading text",
      "noMatches": "No flash cards match your search.",
      "edit": "Edit flashcard",
      "actions": "Card actions",
      "learnAgain": "Learn again",
      "reviewLevelUp": "Review with level up",
      "preview": "Preview reading: {text}",
      "untitledReading": "Untitled reading",
      "showQuestion": "Show question: {text}",
      "showAnswer": "Show answer: {text}",
      "card": "Flashcard",
      "legend": "Points (pt):",
      "closePreview": "Close article preview",
      "articleText": "Article text",
      "results": "{count} of {total} cards",
      "clearSearch": "Clear search",
      "gradeShort": "G"
    },
    "stats": {
      "thisDrill": "this drill",
      "accessibleTitle": "Statistics for {name}",
      "title": "Overview",
      "cards": "Cards Count",
      "repeats": "Avg Repeats",
      "score": "Avg Points",
      "grade": "Avg Grade",
      "progressAria": "Practice summary progress",
      "progress": "Practice Progress",
      "progressDescription": "Cards move forward as your memory gets stronger.",
      "cardCount": "{count} cards",
      "practicing": "Practicing",
      "testing": "Testing",
      "complete": "Complete",
      "composition": "{practicing} practicing, {testing} testing, {completed} complete",
      "addMore": "Add more cards to keep your practice pool strong."
    },
    "levelUp": {
      "title": featureNames.en.levelUp,
      "subtitle": "Turn familiar expressions into your own sentences",
      "readyOne": "{count} card ready to level up",
      "readyMany": "{count} cards ready to level up",
      "loadError": "Failed to load upgradeable cards",
      "saveError": "Failed to level up card",
      "refresh": "Refresh {title}",
      "shuffle": "Show another {title} selection",
      "selectionCount": "Showing {count} of {total} items",
      "sectionEmpty": "No cards here right now.",
      "loading": "Loading eligible cards...",
      "emptyTitle": "No cards are ready yet",
      "emptyHint": "Short cards qualify above 150 pt, Sentence cards above 400 pt, and Reading sentences above 60 repetitions.",
      "readyTitle": "Ready to level up",
      "readyHint": "Choose a card and use its expressions to write a new sentence. Save it in another note to keep practicing.",
      "blueReady": "Create a new Blue card from a Level 1 card",
      "higherLevels": "Level 2–8 → next card",
      "higherTraining": "Level 3–8 cards in training",
      "redReady": "Create the next card from a Level 2–8 card",
      "upgradedTitle": "In training",
      "upgradedHint": "Preview only here. Continue learning in the card's note. Completed cards are not shown.",
      "blueList": "Level 2 · Blue cards",
      "redList": "Level 3–8 · Red cards",
      "inTraining": "Level {level} cards in training",
      "editorTitle": "Create a new Level {level} card",
      "transition": "Level {from} to Level {to}",
      "howItWorks": "What is this lesson?",
      "purpose": "Use expressions from a sentence you have learned to write a new one. Practice your new sentence and revisit the original expressions to become more confident using them.",
      "preservesSource": "Leveling up creates a separate card one level higher. The original card's content, points, and learning history stay intact.",
      "eligibilityTitle": "When is a card ready?",
      "eligibilityLimit": "Each source can create one writing level-up card. Levels stop at 8; Level 8 cards can create another Level 8 card. Reading previews open a sentence picker; each eligible sentence counts separately.",
      "chooseCard": "Choose a card to create",
      "previewOnly": "Preview only",
      "createNext": "Create a Level {level} card",
      "chooseReading": "Choose a sentence",
      "editorHint": "You are preparing a new card, not editing the original. Its learning starts fresh in the destination note.",
      "closeEditor": "Close level up editor",
      "readingSentences": "Reading sentences",
      "chooseSentence": "Choose one sentence to turn into a new card.",
      "closePicker": "Close sentence picker",
      "pushBeyond": "Push Beyond",
      "trial": "Level Up Trial",
      "form": {
        "guidance": "Use the original sentence's expressions to write a new sentence for a different situation, with a matching question or prompt. You can also keep the text unchanged for more practice. Only the new card uses these edits.",
        "cardType": "New card type",
        "short": "Short",
        "sentence": "Sentence",
        "question": "Question",
        "answer": "Answer",
        "questionPlaceholder": "Question or prompt for the new card",
        "answerPlaceholder": "Answer or expression to practice",
        "before": "Context - Before",
        "after": "Context - After",
        "beforePlaceholder": "Situation or preceding line (optional)",
        "afterPlaceholder": "Following line or response (optional)",
        "clearBefore": "Clear context before",
        "clearAfter": "Clear context after",
        "frontMemo": "Front memo",
        "backMemo": "Back memo",
        "frontMemoPlaceholder": "Hint for the answer (optional)",
        "backMemoPlaceholder": "Explanation or grammar note (optional)",
        "formatting": "Markup formatting tools",
        "bold": "Bold",
        "italic": "Italic",
        "copy": "Copy",
        "destination": "Save the new card to",
        "destinationType": "Choose destination note type",
        "destinationHint": "Only other inactive notes are listed. New notes also start inactive. Open the saved note from My learning notes on Home and enable it when you are ready to practice.",
        "existing": "Existing note",
        "new": "New note",
        "newName": "New note name",
        "create": "Create Level {level} Card",
        "creating": "Creating new card...",
        "cancel": "Cancel",
        "questionBlank": "Question cannot be blank.",
        "answerBlank": "Answer cannot be blank.",
        "questionLong": "Question cannot exceed 500 characters.",
        "answerLong": "Answer cannot exceed 500 characters.",
        "contextLong": "Memo and context fields cannot exceed 500 characters.",
        "destinationRequired": "Choose a target note or enter a new note name.",
        "copyError": "Could not copy text to clipboard.",
        "submitError": "No submit action is available."
      }
    },
    "tournament": {
      "title": featureNames.en.tournament,
      "subtitle": "{timezone} monthly arena",
      "loading": "Loading tournament...",
      "loadingStatus": "Loading tournament",
      "signupOpen": "Signup is open",
      "activeStatus": "{phase} - Day {day}/7",
      "competition": "Competition",
      "resultsReady": "Final results are ready",
      "cancelledStatus": "This month was cancelled",
      "nextStatus": "Next tournament schedule",
      "roundOf16": "Round of 16",
      "quarterFinal": "Quarter-final",
      "semiFinal": "Semi-final",
      "final": "Final",
      "participant": "Participant",
      "out": "Out",
      "player": "Player",
      "npc": "NPC",
      "day": "Day {day}",
      "tie": "Tie broken by judges",
      "applyError": "Failed to apply",
      "yourFighter": "Your Fighter",
      "slots": "{count}/32 players",
      "confirmed": "Entry confirmed",
      "apply": "Apply to Fight",
      "nickname": "Tournament nickname",
      "nicknamePlaceholder": "Your tournament nickname",
      "saving": "Saving...",
      "join": "Join Tournament",
      "fightersTitle": "Fighter List",
      "participants": "{players} players, {npcs} NPCs",
      "yourGroup": "Your Group {group}",
      "todayReady": "Today match ready",
      "noMatch": "No match today",
      "rank": "Rank",
      "fighter": "Fighter",
      "wins": "W",
      "matches": "Matches",
      "latest": "Latest and today",
      "finalMatch": "Final Match",
      "tbd": "TBD",
      "versus": "{home} versus {away}",
      "bracket": "Tournament Bracket",
      "bracketDescription": "Tournament ladder from round of 16 to semi-final",
      "results": "Final Results",
      "loadError": "Failed to load tournament",
      "players": "Players",
      "days": "Days",
      "currentDay": "Current day",
      "fighters": "Fighters",
      "nextSchedule": "Next Schedule",
      "signupOpens": "Signup opens {date}",
      "scheduleHint": "The arena opens 3 days before the tournament.",
      "cancelled": "Competition Cancelled",
      "noApplicants": "No applicants",
      "cancelledHint": "No player entered this month, so the arena stayed quiet.",
      "last": "Last Tournament"
    },
    "triumph": {
      "title": featureNames.en.triumph,
      "subtitle": "Keep your completed sentences here and revisit them for review.",
      "lockedTitle": "Your sentence archive is empty for now.",
      "lockedHint": "Keep going. When a card reaches Grade 5, something here will wake up.",
      "statistics": "Completed card statistics",
      "total": "Total {count}",
      "count": "Count",
      "averageDays": "Average days",
      "averageRepeats": "Average reps",
      "averageScore": "Average points",
      "search": "Search Q or A",
      "favorites": "Favorites",
      "perPageLabel": "Cards per page",
      "perPage": "{count} / page",
      "review": "Review",
      "noMatches": "No completed cards match this search.",
      "reps": "{count} reps",
      "score": "{count} pt",
      "createLevelUp": "Create a level-up card",
      "removeFavorite": "Remove favorite",
      "addFavorite": "Add favorite",
      "closeLevelUp": "Close level-up form",
      "days": "{count}d",
      "loading": "Opening Sentence Archive..."
    }
  },
  "ko": {
    "common": {
      "backHome": "훈련소 홈으로",
      "level": "레벨 {level}",
      "score": "포인트",
      "scoreValue": "{score} pt",
      "unknownDrill": "알 수 없는 드릴",
      "reading": "리딩",
      "short": "짧은 표현",
      "sentence": "문장",
      "grade": "학습 등급 {grade}",
      "retry": "다시 시도",
      "speech": "대화",
      "story": "이야기",
      "previous": "이전",
      "next": "다음"
    },
    "map": {
      "empty": "아직 카드가 없습니다. 첫 카드를 만들어 학습을 시작하세요!",
      "searchLabel": "질문·답변 검색:",
      "searchRegion": "카드 검색",
      "searchPlaceholder": "질문·답변·리딩 본문 검색",
      "noMatches": "검색어에 맞는 카드가 없습니다.",
      "edit": "카드 수정",
      "actions": "카드 메뉴",
      "learnAgain": "한번 더 학습",
      "reviewLevelUp": "레벨업으로 복습",
      "preview": "리딩 미리보기: {text}",
      "untitledReading": "제목 없는 리딩",
      "showQuestion": "질문 보기: {text}",
      "showAnswer": "답변 보기: {text}",
      "card": "학습 카드",
      "legend": "포인트 (pt):",
      "closePreview": "미리보기 닫기",
      "articleText": "본문",
      "results": "전체 {total}개 중 {count}개",
      "clearSearch": "검색 지우기",
      "gradeShort": "등급"
    },
    "stats": {
      "thisDrill": "이 드릴",
      "accessibleTitle": "{name} 통계",
      "title": "현황",
      "cards": "카드 수",
      "repeats": "평균 반복 횟수",
      "score": "평균 포인트",
      "grade": "평균 학습 등급",
      "progressAria": "학습 진행 현황",
      "progress": "학습 진행",
      "progressDescription": "기억이 단단해질수록 카드도 다음 단계로 나아갑니다.",
      "cardCount": "카드 {count}개",
      "practicing": "연습 중",
      "testing": "테스트 중",
      "complete": "완료",
      "composition": "연습 중 {practicing}개, 테스트 중 {testing}개, 완료 {completed}개",
      "addMore": "카드를 더 추가해 꾸준히 연습하세요."
    },
    "levelUp": {
      "title": featureNames.ko.levelUp,
      "subtitle": "배운 표현을 나만의 문장으로",
      "readyOne": "도전 가능한 카드 {count}장",
      "readyMany": "도전 가능한 카드 {count}장",
      "loadError": "레벨업 가능한 카드를 불러오지 못했습니다.",
      "saveError": "카드를 레벨업하지 못했습니다.",
      "refresh": "{title} 새로 보기",
      "shuffle": "{title} 카드 무작위로 다시 보기",
      "selectionCount": "전체 {total}개 중 {count}개 표시",
      "sectionEmpty": "현재 이 단계의 카드가 없습니다.",
      "loading": "레벨업 가능한 카드를 불러오는 중...",
      "emptyTitle": "아직 레벨업 가능한 카드가 없습니다",
      "emptyHint": "짧은 표현은 150점 초과, 문장은 400점 초과, 리딩 문장은 60회 초과 반복하면 레벨업할 수 있습니다.",
      "readyTitle": "도전 가능",
      "readyHint": "카드를 골라 기존 표현으로 새 문장을 영작하고, 다른 노트에 저장해 학습을 이어가세요.",
      "blueReady": "레벨 1 카드를 바탕으로 새 블루 카드를 만듭니다",
      "higherLevels": "레벨 2–8 → 다음 카드",
      "higherTraining": "레벨 3–8 카드 학습 중",
      "redReady": "레벨 2–8 카드를 바탕으로 다음 카드를 만듭니다",
      "upgradedTitle": "학습 중",
      "upgradedHint": "여기서는 미리보기만 가능합니다. 카드가 있는 노트에서 학습을 이어가세요. 완료 카드는 표시하지 않습니다.",
      "blueList": "레벨 2 · 블루 카드",
      "redList": "레벨 3–8 · 레드 카드",
      "inTraining": "학습 중인 레벨 {level} 카드",
      "editorTitle": "새 레벨 {level} 카드 만들기",
      "transition": "레벨 {from} → 레벨 {to}",
      "howItWorks": "이 학습은 무엇인가요?",
      "purpose": "기존 문장에서 배운 표현을 활용해 새로운 문장을 영작해 보세요. 만든 문장을 반복 학습하고 기존 표현도 더 깊이 익히며, 다양한 상황에서 자유롭게 쓸 수 있도록 숙달하는 과정입니다.",
      "preservesSource": "레벨업하면 한 단계 높은 별도 카드가 만들어집니다. 원본 카드의 내용·포인트·학습 기록은 그대로 유지됩니다.",
      "eligibilityTitle": "어떤 카드가 도전할 수 있나요?",
      "eligibilityLimit": "원본 카드마다 영작 레벨업을 한 번 할 수 있습니다. 레벨은 최대 8이며, 레벨 8에서는 새 레벨 8 카드를 만듭니다. 리딩은 미리보기를 누른 뒤 문장을 선택하며, 조건을 충족한 각 문장을 별도로 셉니다.",
      "chooseCard": "카드를 눌러 만들기",
      "previewOnly": "미리보기 전용",
      "createNext": "레벨 {level} 카드 만들기",
      "chooseReading": "문장 선택하기",
      "editorHint": "원본을 편집하는 것이 아니라 새 카드를 준비하는 단계입니다. 새 카드의 학습은 저장할 노트에서 처음부터 시작합니다.",
      "closeEditor": "레벨업 편집 닫기",
      "readingSentences": "리딩 문장",
      "chooseSentence": "새 카드로 만들 문장을 하나 선택하세요.",
      "closePicker": "문장 선택 닫기",
      "pushBeyond": "한계를 넘어",
      "trial": "레벨업 도전",
      "form": {
        "guidance": "기존 문장의 표현을 활용해 다른 상황의 새 문장을 영작하고, 그에 맞는 질문이나 힌트를 적어 보세요. 같은 문장을 더 익히고 싶다면 내용을 그대로 사용해도 됩니다. 수정한 내용은 새 카드에만 적용됩니다.",
        "cardType": "새 카드 유형",
        "short": "짧은 표현",
        "sentence": "문장",
        "question": "질문",
        "answer": "답변",
        "questionPlaceholder": "새 카드에서 사용할 질문이나 힌트",
        "answerPlaceholder": "연습할 답변이나 표현",
        "before": "이전 맥락",
        "after": "이후 맥락",
        "beforePlaceholder": "앞선 상황이나 대사 (선택)",
        "afterPlaceholder": "이어서 나오는 대사나 응답 (선택)",
        "clearBefore": "이전 맥락 지우기",
        "clearAfter": "이후 맥락 지우기",
        "frontMemo": "질문 메모",
        "backMemo": "답변 메모",
        "frontMemoPlaceholder": "답변을 떠올릴 힌트 (선택)",
        "backMemoPlaceholder": "표현이나 문법 설명 (선택)",
        "formatting": "텍스트 서식 도구",
        "bold": "굵게",
        "italic": "기울임",
        "copy": "복사",
        "destination": "새 카드를 저장할 노트",
        "destinationType": "저장할 노트 유형 선택",
        "destinationHint": "원본을 제외한 비활성 노트만 표시됩니다. 새 노트도 비활성 상태로 만들어집니다. 학습할 준비가 되면 홈의 내 학습 노트에서 저장한 노트를 열고 활성화하세요.",
        "existing": "기존 노트",
        "new": "새 노트",
        "newName": "새 노트 이름",
        "create": "레벨 {level} 카드 생성",
        "creating": "새 카드 생성 중...",
        "cancel": "취소",
        "questionBlank": "질문을 입력해 주세요.",
        "answerBlank": "답변을 입력해 주세요.",
        "questionLong": "질문은 500자 이내로 입력해 주세요.",
        "answerLong": "답변은 500자 이내로 입력해 주세요.",
        "contextLong": "메모와 맥락은 각각 500자 이내로 입력해 주세요.",
        "destinationRequired": "저장할 노트를 선택하거나 새 노트 이름을 입력해 주세요.",
        "copyError": "클립보드에 복사하지 못했습니다.",
        "submitError": "카드 생성 기능을 사용할 수 없습니다."
      }
    },
    "tournament": {
      "title": featureNames.ko.tournament,
      "subtitle": "{timezone} 기준 월간 대회",
      "loading": "대회를 불러오는 중...",
      "loadingStatus": "대회를 불러오는 중",
      "signupOpen": "참가 신청 중",
      "activeStatus": "{phase} · {day}/7일차",
      "competition": "대회",
      "resultsReady": "최종 결과가 나왔습니다",
      "cancelledStatus": "이번 달 대회가 취소되었습니다",
      "nextStatus": "다음 대회 일정",
      "roundOf16": "16강",
      "quarterFinal": "8강",
      "semiFinal": "4강",
      "final": "결승",
      "participant": "참가자",
      "out": "탈락",
      "player": "플레이어",
      "npc": "NPC",
      "day": "{day}일차",
      "tie": "심판 판정으로 승부 결정",
      "applyError": "참가 신청을 완료하지 못했습니다.",
      "yourFighter": "나의 선수",
      "slots": "플레이어 {count}/32명",
      "confirmed": "참가 확정",
      "apply": "대회 참가 신청",
      "nickname": "대회 닉네임",
      "nicknamePlaceholder": "대회에서 사용할 닉네임",
      "saving": "저장 중...",
      "join": "대회 참가하기",
      "fightersTitle": "선수 목록",
      "participants": "플레이어 {players}명, NPC {npcs}명",
      "yourGroup": "내 조 {group}",
      "todayReady": "오늘의 경기 준비 완료",
      "noMatch": "오늘은 경기가 없습니다",
      "rank": "순위",
      "fighter": "선수",
      "wins": "승",
      "matches": "경기",
      "latest": "최근 경기와 오늘의 경기",
      "finalMatch": "결승전",
      "tbd": "미정",
      "versus": "{home} 대 {away}",
      "bracket": "대진표",
      "bracketDescription": "16강부터 4강까지의 대진표",
      "results": "최종 결과",
      "loadError": "대회를 불러오지 못했습니다.",
      "players": "플레이어",
      "days": "대회 기간",
      "currentDay": "진행 일차",
      "fighters": "전체 선수",
      "nextSchedule": "다음 일정",
      "signupOpens": "참가 신청 시작: {date}",
      "scheduleHint": "대회 시작 3일 전부터 신청할 수 있습니다.",
      "cancelled": "대회 취소",
      "noApplicants": "참가 신청자 없음",
      "cancelledHint": "이번 달은 참가 신청자가 없어 대회가 열리지 않았습니다.",
      "last": "지난 대회"
    },
    "triumph": {
      "title": featureNames.ko.triumph,
      "subtitle": "학습을 완료한 문장을 모아두고 다시 복습하세요.",
      "lockedTitle": "아직 문장 보관함에 문장이 없습니다.",
      "lockedHint": "계속 도전하세요. 카드가 학습 등급 5에 도달하면 이곳에 기록됩니다.",
      "statistics": "완료 카드 통계",
      "total": "전체 {count}",
      "count": "카드 수",
      "averageDays": "평균 소요 일수",
      "averageRepeats": "평균 반복 횟수",
      "averageScore": "평균 포인트",
      "search": "질문이나 답변 검색",
      "favorites": "즐겨찾기",
      "perPageLabel": "페이지당 카드 수",
      "perPage": "페이지당 {count}개",
      "review": "복습",
      "noMatches": "검색 조건에 맞는 완료 카드가 없습니다.",
      "reps": "{count}회 반복",
      "score": "{count} pt",
      "createLevelUp": "레벨업 카드 만들기",
      "removeFavorite": "즐겨찾기 해제",
      "addFavorite": "즐겨찾기 추가",
      "closeLevelUp": "레벨업 양식 닫기",
      "days": "{count}일",
      "loading": "문장 보관함을 여는 중..."
    }
  }
};

export default learningNavigation;
